import { AnimatePresence, motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import classNames from "classnames"
import useContainerWidth from "@/hooks/useContainerWidth"
import { ContentModerationRating } from "@/api/sdk"
import NsfwBlurImage from "@/components/NsfwBlurImage"
import { QueryKey } from "@tanstack/react-query"

export interface BaseImageInViewProps {
  onClick?: () => void
  isDisabledInView?: boolean
  containerWidth?: number
  stretch?: boolean
  showHoverAttributes?: boolean
  href?: string
  hiddenMenu?: boolean
  queryKey?: QueryKey
  selectionMode?: boolean
  isChecked?: boolean
  isExplore?: boolean
  onCheck?: () => void
}

export interface ImageModeration {
  moderationRating?: ContentModerationRating
  url?: string
  width?: number
  height?: number
}

export interface ImageDimensions {
  width?: number | null
  height?: number | null
}

interface ImageInViewBaseProps extends BaseImageInViewProps {
  children: React.ReactNode
  imageDimensions?: ImageDimensions
  moderationRating?: ContentModerationRating
  gaEventData?: {
    id: string
    name: string
    tab_name: string
  }
  isShow?: boolean
  setIsShow?: (isShow: boolean) => void
}

export const ImageInViewBase = ({
  children,
  imageDimensions,
  isDisabledInView,
  containerWidth,
  stretch,
  isExplore,
  moderationRating,
  selectionMode,
  isChecked,
  gaEventData,
  isShow,
  setIsShow,
  ...props
}: ImageInViewBaseProps) => {
  const imageItemRef = useRef<HTMLDivElement | null>(null)
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "800px 0px 800px 0px",
  })
  const [imageSize, setImageSize] = useState(0)
  const widthCard = useContainerWidth(imageItemRef, true)

  useEffect(() => {
    if (isDisabledInView) return
    if (containerWidth && imageDimensions?.width && imageDimensions.height && widthCard) {
      const height = (imageDimensions.height / imageDimensions.width) * (widthCard || 250)
      setImageSize(height)
    }
  }, [containerWidth, imageDimensions, widthCard, isDisabledInView])

  return (
    <div
      ref={r => {
        imageItemRef.current = r
        ref(r)
      }}
      className={classNames(
        "image-item relative overflow-hidden rounded-2xl transform-gpu bg-atherGray-900 shadow-sm shadow-whiteAlpha-200",
      )}
      style={{
        minHeight: "10rem",
        height: stretch ? "100%" : isDisabledInView ? "auto" : imageSize,
      }}
      {...props}
    >
      {selectionMode && (
        <div
          className={classNames("absolute w-full h-full top-0 left-0 border-[2px] pointer-events-none rounded-2xl", {
            "border-atherPurple-500": isChecked,
            "border-[transparent]": !isChecked,
            "z-0": !selectionMode,
            "z-[1]": selectionMode,
          })}
        />
      )}
      <AnimatePresence mode="wait">
        {(inView || isDisabledInView) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-center justify-center w-full flex-col overflow-hidden"
            style={{
              minHeight: "10rem",
              height: stretch ? "100%" : isDisabledInView ? "auto" : imageSize,
            }}
          >
            {children}

            {/* NSFW Blur Controls */}
            {(isExplore || isShow) &&
              inView &&
              moderationRating &&
              (moderationRating === ContentModerationRating.HardcoreNsfw ||
                moderationRating === ContentModerationRating.SemiNsfw) && (
                <NsfwBlurImage
                  isShow={isShow ?? false}
                  moderationRating={moderationRating}
                  onToggleShow={setIsShow ?? (() => {})}
                  gaEvent={{
                    tab_name: gaEventData?.tab_name ?? "",
                    params: {
                      [`${gaEventData?.tab_name}_id`]: gaEventData?.id ?? "",
                      [`${gaEventData?.tab_name}_name`]: gaEventData?.name ?? "",
                    },
                  }}
                />
              )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
